/**
 * Location Alert — soft region-mismatch prompt in the header.
 *
 * Detection is per-visitor, so it must run client-side: the page HTML is
 * full-page cached and identical for everyone. We derive the market from the
 * browser timezone — no /api/geo.php round-trip (that cold server call sat on the
 * critical path and delayed this reveal, hurting Speed Index). CZ/SK zones map to
 * the CZ market; any other real zone resolves to the default (Global) market; an
 * unknown/empty zone suggests nothing. If the suggested market differs from the
 * one being viewed and the visitor hasn't dismissed it this session, the bar
 * slides out from behind the header pill.
 */

const DISMISS_KEY = 'webtero_geo_dismissed';

// Sentinel country for a real-but-unmapped timezone → resolves to the default market.
const OTHER = '__other__';

// Minimal timezone → country map. Only countries that map to a non-default market
// matter here; anything else resolves to the default/Global market.
const TZ_COUNTRY = {
  'Europe/Prague': 'CZ',
  'Europe/Bratislava': 'SK',
};

function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
}

export function locationAlert() {
  const el = document.querySelector('[data-location-alert]');
  if (!el) return;

  // Dismissed (close or continue) earlier this session → stay hidden.
  try {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
  } catch (e) {
    /* sessionStorage unavailable — just proceed */
  }

  const currentMarket = el.dataset.currentMarket || '';
  let markets = [];
  try {
    markets = JSON.parse(el.dataset.markets || '[]');
  } catch (e) {
    return;
  }
  if (!markets.length) return;

  const defaultMarket = (markets.find((m) => m.url === '/') || markets[0]).code;

  // Resolve a country code to a market: a configured match wins, an unmapped
  // (but real) country falls back to the default market, and null stays null.
  const marketForCountry = (country) => {
    if (!country) return null;
    const hit = markets.find((m) => (m.country_codes || []).includes(country));
    return hit ? hit.code : defaultMarket;
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      /* ignore */
    }
  };

  const reveal = (suggestedCode) => {
    const select = el.querySelector('[data-location-alert-select]');
    const flagImg = el.querySelector('[data-location-alert-flag]');
    const continueBtn = el.querySelector('[data-location-alert-continue]');
    const closeBtn = el.querySelector('[data-location-alert-close]');

    // Keep the flag image in sync with the selected region (SVGs in /icons/flags).
    const syncFlag = () => {
      if (!select || !flagImg) return;
      const o = select.options[select.selectedIndex];
      if (o && o.dataset.flag) flagImg.src = `/icons/flags/${o.dataset.flag}.svg`;
    };

    // Pre-select the suggested market.
    if (select) {
      const opt = select.querySelector(`option[data-market="${suggestedCode}"]`);
      if (opt) select.value = opt.value;
      select.addEventListener('change', syncFlag);
    }
    syncFlag();

    // Continue → go to the chosen market, remembering the dismissal.
    if (continueBtn) {
      continueBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const url = select ? select.value : continueBtn.getAttribute('href');
        dismiss();
        if (url) window.location.href = url;
      });
    }

    // Close → hide for the rest of this session.
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        dismiss();
        el.classList.remove('is-visible');
        setTimeout(() => el.classList.add('hidden'), 300);
      });
    }

    el.classList.remove('hidden');
    void el.offsetWidth; // force reflow so the slide-in transition runs
    el.classList.add('is-visible');
  };

  const decide = (suggested) => {
    if (suggested && suggested !== currentMarket) reveal(suggested);
  };

  // Resolve the market synchronously from the browser timezone — no network request.
  const tz = browserTimezone();
  const country = TZ_COUNTRY[tz] || (tz ? OTHER : null);
  decide(marketForCountry(country));

  // --- KEPT FOR FUTURE: server-side IP detection via /api/geo.php (Cloudflare
  // CF-IPCountry → Accept-Language, resolved in Env), with the timezone as fallback.
  // Taken off the critical path because the cold server call (~1.3s on this host) sat in
  // front of this reveal and inflated Speed Index. To restore, comment the three lines
  // above and uncomment the block below. (public/api/geo.php is still in place.)
  //
  // fetch('/api/geo.php', { headers: { Accept: 'application/json' } })
  //   .then((r) => (r.ok ? r.json() : null))
  //   .then((data) => {
  //     if (data && data.market) { decide(data.market); return; }
  //     const t = browserTimezone();
  //     decide(marketForCountry(TZ_COUNTRY[t] || (t ? OTHER : null)));
  //   })
  //   .catch(() => {
  //     const t = browserTimezone();
  //     decide(marketForCountry(TZ_COUNTRY[t] || (t ? OTHER : null)));
  //   });
}
